import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import "../styles.css";

export default function TrophyTracker() {
  return (
    <>
      <SEO
        title="Trophy Tracker - PS5 Trophy Counter for Streamers"
        description="Transform every PS5 trophy pop-up into a live on-stream achievement counter! Learn how to install and customize the Trophy Tracker for your stream."
        keywords="Trophy Tracker, PS5 trophies, stream overlay, trophy counter, Streamer.bot, OBS overlay, PlayStation trophies, trophy hunter"
        url="https://zemaxi.com/tools/trophy-tracker"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="trophy-tracker-hero">
          <div className="container">
            <h1>Trophy <span className="highlight">Tracker</span></h1>
            <p>Transform every PS5 trophy pop-up into a live on-stream achievement counter!</p>
          </div>
        </section>

        <section className="trophy-tracker-content">
          <div className="container">
            <div className="trophy-tracker-image">
              <img src="https://zemaxi.com/images/products/TrophyTracker.png" alt="Trophy Tracker Preview" />
            </div>

            <div className="trophy-tracker-description">
              <h2>What is <span className="highlight">Trophy Tracker?</span></h2>
              <p>The Trophy Tracker automatically detects and tracks your PS5 trophy unlocks in real-time! Every time you earn a trophy, your stream overlay updates instantly, showing your viewers your progress throughout your gaming session. Perfect for Trophy Hunters and streamers who want to showcase their achievements.</p>
            </div>

            <div className="trophy-tracker-video">
              <h2>Video <span className="highlight">Tutorial</span></h2>
              <div className="video-embed-container">
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/H-_EGk-4bMw?rel=0"
                  title="Trophy Tracker Installation Guide"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            <div className="trophy-tracker-install">
              <h2>Setting up the <span className="highlight">Trophy Tracker</span></h2>
              <p>Follow these steps to set up Trophy Tracker on your stream. I recommend following the YouTube tutorial above which will guide you better.</p>

              <div className="install-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Download Trophy Tracker</h3>
                  <p>If you haven't already, download the Trophy Tracker from <a href="files/trophy-tracker.zip" target="_blank" rel="noopener noreferrer">here</a> and un-zip the contents into a folder on your computer such as C:\Streaming\trophy-tracker. You should see two files called TrophyTracker.exe and trophy_tracker.lua and the templates folder.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step1.png" alt="Trophy Tracker Download and Extract" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Enable OBS Websockets Server</h3>
                  <p>Launch OBS Studio and then click on Tools and then WebSocket Server Settings and then make sure you have the Enable WebSocket Server checked.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step2.png" alt="Enable WebSocket Server" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Add the Trophy Tracker Lua Script</h3>
                  <p>Click on Tools and then Scripts and then the + icon to add a new script. Browse to the location of the trophy_tracker.lua file and select it.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step3.png" alt="Add Trophy Tracker LUA Script" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Choose your Settings</h3>
                  <p>Navigate through the settings and customize your Trophy Tracker to your liking.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step4.png" alt="Add Browser Source to OBS" />
                  </div>
                  <p>If you don't want to use Streamer.bot and the Trophy Tracker Customizer, leave the Output Mode to Local OBS Text Source.</p>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">5</div>
                <div className="step-content">
                  <h3>Recommended Settings</h3>
                  <p>Assuming you have a Capture Card, then I recommend selecting the Capture Card source from the Capture Source dropdown.</p>
                  <p>For Cooldown, this is the amount of time in seconds that the Trophy Tracker will wait after it tracked a trophy pop up. Typically PS5 trophy alerts last 5.5 seconds so I recommend changing it to 6 seconds.</p>
                  <p>If you are going to use Streamer.bot which the tracker then I recommend changing Output Mode to Streamer.bot Websocket.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step5.png" alt="Recommended Settings" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">6</div>
                <div className="step-content">
                  <h3>Select the Detection Region</h3>
                  <p>This is important. The Trophy Tracker needs to know where on your source the trophy pop up will appear. Click on Select Detection Region and a window will pop up showing you a screenshot of your source.</p>
                  <p>I have prepared a template under /templates/PS5-Trophy-Template.jpg that you can use to help you have a visual reference.</p>
                  <p>Select your region by dragging the mouse over the green rectangle and then press Enter key. And you should be set. Make sure you Enable Trophy Tracking on the Lua Script.</p>

                  <div className="detection-options">
                    <div className="detection-option">
                      <h4>Option A: Capture Card</h4>
                      <p>If you are using a Capture Card, the Detection Region window will show a screenshot of your Capture Card source. Use the mouse to select the area where the trophy pop up will appear (Top right area).</p>
                      <p>Leave the Detection Engine window open and adjust the size of the template image to match the size of your Capture Card source.</p>
                      <div className="step-screenshot">
                        <img src="/images/trophy-tracker/trophy-tracker-step6-capturecard.png" alt="Trophy Tracker Detection - Capture Card" />
                      </div>
                    </div>
                    <div className="detection-option">
                      <h4>Option B: Screen Capture</h4>
                      <p>If you don't have a Capture card and instead use applications like PS Remote Play, You can select the Screen Capture option to capture your Remote Play window. Note: This will capture the entire monitor. The Detection Region window will show up as blue rectangle.</p>
                      <div className="step-screenshot">
                        <img src="/images/trophy-tracker/trophy-tracker-step6-screencapture.png" alt="Trophy Tracker Detection - Screen Capture" />
                      </div>
                      <br></br>
                      <p>It's important to note that if you use Screen Capture to capture your OBS Studio set up, do not move your OBS Studio application around. Keep it maximised and make sure it is always in view as the trophy tracker will be looking for the trophy pop up in that area.</p>
                      <p>I recommend adding the PS5 Trophy Template as an Image Source in OBS and then using that to help set your Detection Region.</p>

                    </div>
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">7</div>
                <div className="step-content">
                  <h3>Set your OBS GDI Text <small>(Ignore this if you will be using Streamer.bot and Trophy Tracker Customizer)</small></h3>
                  <p>Create a new Text Source called "TT_PS5" and set the text to "0". You can customise the design of the text source to your liking. This is only if you want to use the Trophy Tracker without Streamer.bot and Trophy Tracker Customizer.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step7.png" alt="Trophy Tracker Customizer" />
                  </div>
                </div>
              </div>
            </div>

            <div className="trophy-tracker-install">
              <h2>Streamer.bot <span className="highlight">Integration</span></h2>
              <p>Want to take your Trophy Tracker to the next level? Integrate it with Streamer.bot for custom overlays, alerts, and even PSN Integration!</p>

              <div className="install-step">
                <div className="step-number">8</div>
                <div className="step-content">
                  <h3>Download Streamer.bot</h3>
                  <p>If you haven't already, download <a href="https://streamer.bot" target="_blank" rel="noopener noreferrer">Streamer.bot</a>. This is the automation tool that will connect with Trophy Tracker and allow you to customise your trophy pop up alerts.</p>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">9</div>
                <div className="step-content">
                  <h3>Enable the Websocket Server in Streamer.bot</h3>
                  <p>In Streamer.bot, go to the Servers/Clients, WebsSocket Server and make sure Auto-Start is On and then Start the Server.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step9.png" alt="Enable Websocket Server in Streamer.bot" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">10</div>
                <div className="step-content">
                  <h3>Import the Trophy Tracker Streamer.bot File</h3>
                  <p>Click on Import at the top and drag the trophy-tracker.mbt file into the Import String.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step10-1.png" alt="Import the Trophy Tracker Streamer.bot File" />
                  </div>
                  <br></br>
                  <p>Once you dragged the file into the Import String, make sure you see the Actions in the list, and then click on Import.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step10-2.png" alt="Verify the Trophy Tracker Streamer.bot File" />
                  </div>
                  <br></br>
                  <p>Once imported, you should see the Actions in your Streamer.bot. Nothing needs to be edited so you can minimize them.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step10-3.png" alt="Verify the Trophy Tracker Actions" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">11</div>
                <div className="step-content">
                  <h3>Use the Trophy Tracker Customizer</h3>
                  <p>Head over to the <a href="https://trophytracker.zemaxi.com/" target="_blank" rel="noopener noreferrer">Trophy Tracker Customizer</a> to design your overlay. It should say "Connected" in the Preview area. Once you have choosen your design, click on "Generate" at the top and then Copy the URL and paste it as a Browser Source in OBS and you're all set!</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step11.png" alt="Trophy Tracker Customizer" />
                  </div>
                </div>
              </div>
            </div>

            <div className="trophy-tracker-install">
              <h2>PSN <span className="highlight">Integration</span></h2>
              <p>To maximize the trophy tracker experience, you can use the PSN Integration feature. Along with tracking your trophies, this will allow you to track trophies Per-Game and has fail safe capabilities in case the tracker doesn't detect your trophy pop up.</p>

              <div className="install-step">
                <div className="step-number">12</div>
                <div className="step-content">
                  <h3>Log in to PlayStation</h3>
                  <p>Before you can use the PSN Integration, you will need to have a PSN account and already own the game you want to track. Login to your <a href="https://www.playstation.com" target="_blank" rel="noopener noreferrer">PlayStation Account</a> and then head back to the Trophy Tracker Customizer.</p>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">13</div>
                <div className="step-content">
                  <h3>Get your NPSSO Token</h3>
                  <p>Head over to the <a href="https://trophytracker.zemaxi.com/" target="_blank" rel="noopener noreferrer">Trophy Tracker Customizer</a> and where it says NPSSO Token, click on the "PlayStation" link.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step13-1.png" alt="Trophy Tracker Customizer" />
                  </div>
                  <br></br>
                  <p>This will open a new tab and you will see the 64-Character Token. Do not share this with anyone. Copy this token and paste it into the NPSSO Token field in the Trophy Tracker Customizer. Note: NPSSO Tokens expire after 2 months so you will need to redo this again in the future.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step13-2.png" alt="NPSSO Token" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">14</div>
                <div className="step-content">
                  <h3>Verify your NPSSO Token</h3>
                  <p>Once you have pasted your NPSSO Token, Click on the "Connect to PSN" button and you should get a pop-up that says it was successful.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step14.png" alt="Trophy Tracker Customizer" />
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">15</div>
                <div className="step-content">
                  <h3>Search Your Game</h3>
                  <p>You can now search for a game you will be streaming/playing. Start typing the name such as "Tomb Raider" or "Avatar" and click on the Search button. You should see the game pop up in the list.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step15-1.png" alt="Trophy Tracker Customizer" />
                  </div>
                  <br></br>
                  <p>Select your game and you will see the real amount of trophies you have earned and the Trophy counter will now sync with PSN. You can now customize the trophy tracker to your liking and then Generate the URL and Copy/Paste it to your OBS Studio as a Browser Source.</p>
                  <div className="step-screenshot">
                    <img src="/images/trophy-tracker/trophy-tracker-step15-2.png" alt="Trophy Tracker Customizer" />
                  </div>
                </div>
              </div>


            </div>

            <div className="trophy-tracker-cta">
              <h2>Ready to <span className="highlight">Get Started?</span></h2>
              <div className="cta-buttons">
                <a href="/files/trophy-tracker.zip" download className="btn btn-primary">
                  <i className="fas fa-download"></i> Download Trophy Tracker
                </a>
                <a href="https://trophytracker.zemaxi.com" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                  <i className="fas fa-paint-brush"></i> Open Customizer
                </a>
              </div>
              <div className="beta-section">
                <p className="beta-description">
                  <i className="fas fa-info-circle"></i> Steam and Xbox support included in the Beta - Online only
                </p>
                <a href="https://beta.zemaxi.com/achievement-tracker" target="_blank" rel="noopener noreferrer" className="btn btn-beta">
                  <i className="fas fa-flask"></i> Try the Beta Achievement Tracker
                </a>
              </div>
            </div>

            <div className="trophy-tracker-faq">
              <h2>Frequently Asked <span className="highlight">Questions</span></h2>
              <div className="faq-container">
                <div className="faq-item">
                  <h3>What is Trophy Tracker?</h3>
                  <p>Trophy Tracker is a free OBS overlay that automatically detects PlayStation trophy pop-ups on screen and displays a live trophy count on your stream. It works with PS5 (and PS4 but this was only tested using screenshots), and can optionally sync with your real PSN account for accurate counts.</p>
                </div>

                <div className="faq-item">
                  <h3>What do I need to use Trophy Tracker?</h3>
                  <p>You need OBS Studio, Streamer.bot, and the Trophy Tracker application (a small Windows app) along with the Lua script. For PSN integration you'll also need a PlayStation Network account. The overlay is added to OBS as a Browser Source..</p>
                </div>

                <div className="faq-item">
                  <h3>How does the trophy detection actually work?</h3>
                  <p>The detector watches a region of your screen (or a capture card feed) and uses OCR (text recognition) to look for the "Trophy earned!" text that appears when you unlock a trophy. It requires two consecutive detections to avoid false positives, and has a cooldown to prevent double-counting.</p>
                </div>

                <div className="faq-item">
                  <h3>Do I need a capture card?</h3>
                  <p>No. By default the detector captures directly from your monitor. If you play on a TV that isn't your PC monitor, you can use a capture card and point the detector at that OBS source instead. Both modes are supported.</p>
                </div>

                <div className="faq-item">
                  <h3>What is an NPSSO token and why do you need it?</h3>
                  <p>An NPSSO token is a 64-character session cookie from your PlayStation Network account. The Trophy Tracker uses it to talk to the official PSN API so it can verify your real trophy count and pull game/trophy data. It is essentially a temporary login credential for your PSN account.</p>
                </div>

                <div className="faq-item">
                  <h3>How do I get my NPSSO token?</h3>
                  <p>Log in to the PlayStation website in your browser, then visit https://ca.account.sony.com/api/v1/ssocookie. You'll see a JSON response containing your NPSSO token — copy the 64-character string. If you get an error, try a different browser or clear cookies and log in again.</p>
                </div>

                <div className="faq-item">
                  <h3>Is my NPSSO token stored safely? Does it appear in any logs?</h3>
                  <p>Your NPSSO token is stored only in your browser's localStorage on your own machine — it is never written to a file, database, or log. The server that handles PSN requests is fully stateless: it holds your token in memory for up to 55 minutes to avoid re-authenticating on every request, then discards it. The server logs only contain generic error messages (e.g. "Auth error", "Search error") and never log your NPSSO token. No credentials are written to disk on the server side at any point.</p>
                </div>

                <div className="faq-item">
                  <h3>Can the server owner see my NPSSO token?</h3>
                  <p>The NPSSO is sent to the PSN proxy server via an HTTP header (X-PSN-NPSSO). The server code does not log this header. However, as with any third-party server, you are trusting the operator. If you prefer full control, you can ask the server owner for the source code and host it yourself.</p>
                </div>

                <div className="faq-item">
                  <h3>Does my NPSSO token expire?</h3>
                  <p>Yes. NPSSO tokens typically expire after around 60 days, or immediately if you sign out of the PlayStation website. If your token expires, the customizer will detect this automatically and ask you to re-enter a new one. You can get a fresh token by revisiting the ssocookie URL.</p>
                </div>

                <div className="faq-item">
                  <h3>Can I use Trophy Tracker without PSN integration?</h3>
                  <p>Absolutely. PSN integration is entirely optional. Without it, the overlay simply counts trophies based on screen detection. You won't get game names, trophy totals, or progress bars, but the core counter works perfectly fine on its own.</p>
                </div>

                <div className="faq-item">
                  <h3>How do I customise the look of the overlay?</h3>
                  <p>Use the Trophy Tracker Customizer page. You can change the style preset (PlayStation theme, generic, etc.), font, colours, trophy icon type, font size, alignment, and more. It generates a URL with your settings baked in — just paste that URL into an OBS Browser Source.</p>
                </div>

                <div className="faq-item">
                  <h3>My trophy count seems wrong — how does it stay accurate?</h3>
                  <p>When PSN integration is enabled, PSN is the primary source of truth. On startup the overlay immediately queries PSN for your real earned count. After each detection it shows an optimistic +1 instantly, then verifies with PSN a few seconds later and corrects if needed. The count can only go up during verification (it uses the higher of PSN vs. local), so you'll never see it randomly drop.</p>
                </div>

                <div className="faq-item">
                  <h3>Can I track trophies for a specific game?</h3>
                  <p>Yes, if PSN is connected. In the customizer, search for a game by name, select it, and the overlay will show your earned count out of the game's total trophies. You can also enable a progress bar and display the game name on the overlay.</p>
                </div>

                <div className="faq-item">
                  <h3>I switched games mid-stream, do I need to do anything?</h3>
                  <p>If you're tracking a specific game via PSN, you'll want to switch the selected game in the customizer so the total and progress is updated. If you're just using the basic counter without PSN, you will need to go into the Lua Script and click on Reset Trophy Count. However, you will lose the progress on your previous game.</p>
                </div>

                <div className="faq-item">
                  <h3>The detector isn't picking up my trophies, what should I check?</h3>
                  <ul>
                    <li>Make sure the detection region covers where trophy pop-ups appear (usually top-right). You can use the "Select Detection Region" button in OBS to set a custom region.</li>
                    <li>Check that the correct capture source is selected (screen capture vs. a specific OBS source/capture card).</li>
                    <li>Ensure OCR dependencies are installed — the detector will warn you on startup if OCR is unavailable and fall back to less reliable template matching.</li>
                    <li>Try enabling "Save Debug Images" in the settings and check the saved screenshots to see what the detector is actually seeing.</li>
                    <li>Make sure the cooldown isn't set too high (default is 5 seconds).</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
